import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { BillingFrequency, CurrencyType, formatBillingFrequency, OfferingDto, OfferingType, ServiceType } from '@/domain/offerings/offering';
import { enhancedCreateOfferingSchema } from '@/domain/offerings/schemas';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2 } from 'lucide-react';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

interface OfferingFormProps {
  defaultValues?: Partial<OfferingDto>
  onSubmit: SubmitHandler<OfferingDto>
  isSubmitting?: boolean
  submitButtonText?: string
  onCancel?: () => void
}

export const OfferingForm = ({
  defaultValues,
  onSubmit,
  isSubmitting = false,
  submitButtonText = 'Guardar',
  onCancel
}: OfferingFormProps) => {
  const form = useForm<OfferingDto>({
    resolver: zodResolver(enhancedCreateOfferingSchema),
    defaultValues: {
      name: '',
      code: '',
      serviceType: null,
      description: '',
      offeringType: OfferingType.RECURRING,
      billingFrequency: BillingFrequency.MONTHLY,
      basePrice: 0,
      currency: CurrencyType.ARS,
      taxRate: 21,
      isTaxable: false,
      isInvoiceable: true,
      hasMultipleOffered: false,
      hasPaymentPlanAvailable: false,
      hasAgePricing: false,
      isAdditional: false,
      ...defaultValues
    },
    mode: 'onBlur',
    resetOptions: {
      keepValues: true,
    },
  });

  const isOneTimeOffering = form.watch('offeringType') === OfferingType.ONE_TIME;
  const billingFreq = form.watch('billingFrequency')

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isFormValid = await form.trigger([
      'name',
      'code',
      'serviceType',
      'description',
      'offeringType',
      'billingFrequency',
      'basePrice',
      'currency',
      'taxRate',
      'isTaxable',
      'isInvoiceable',
      'hasMultipleOffered',
      'hasPaymentPlanAvailable',
      'hasAgePricing',
      'isAdditional',
    ]);
    if (!isFormValid) {
      return;
    }

    const formValues = form.getValues();
    Promise.resolve()
      .then(() => onSubmit(formValues))
      .catch(error => {
        throw error;
      });
  };

  const frequencyOpts = Object.values(BillingFrequency)
    .filter(freq => (isOneTimeOffering && freq === BillingFrequency.ONE_TIME)
      || (!isOneTimeOffering && (freq === BillingFrequency.MONTHLY || freq === BillingFrequency.ANNUAL)))

  return (
    <Form {...form}>
      <form onSubmit={handleFormSubmit} className="space-y-6" >
        <div className="grid gap-4">
          <FormField
            control={form.control}
            name="serviceType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Servicio *</FormLabel>
                <Select
                  onValueChange={(value: string) => {
                    const serviceType = value as ServiceType | null;
                    field.onChange(serviceType);
                  }}
                  value={field.value ?? ''}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un tipo de servicio" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={ServiceType.CREMACIONES}>Cremaciones</SelectItem>
                    <SelectItem value={ServiceType.ABONOS}>Abonos</SelectItem>
                    <SelectItem value={ServiceType.PARCELAS}>Parcelas</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: Abono de cremación" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: abono-cremacion" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descripción</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Descripción que detalle el servicio ofrecido..."
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="offeringType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo *</FormLabel>
                  <Select
                    onValueChange={(value: OfferingType) => {
                      field.onChange(value)
                      if (value === OfferingType.ONE_TIME) {
                        form.setValue('billingFrequency', BillingFrequency.ONE_TIME)
                      }
                      else if (value === OfferingType.RECURRING) {
                        form.setValue('billingFrequency', BillingFrequency.MONTHLY)
                      }
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={OfferingType.RECURRING}>Recurrente</SelectItem>
                      <SelectItem value={OfferingType.ONE_TIME}>Única vez</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="billingFrequency"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Frecuencia *</FormLabel>
                  <Select
                    onValueChange={(value: BillingFrequency) => field.onChange(value)}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className={cn(fieldState.error && 'border-destructive')}>
                        <SelectValue placeholder="Seleccione frecuencia" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {frequencyOpts.map((freq) => (
                        <SelectItem key={freq} value={freq}>
                          {formatBillingFrequency(freq)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Alert className='col-span-2'>
              <AlertDescription className='flex items-center gap-2 text-muted-foreground'>
                <AlertCircle className='w-4 flex-shrink-0' />
                {!isOneTimeOffering
                  ? `Se generará un débito en la cuenta corriente del cliente de manera 
                ${formatBillingFrequency(billingFreq)}`
                  : 'Se cobrará una única vez'}
              </AlertDescription>
            </Alert>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="basePrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Precio base *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Moneda *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione moneda" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CurrencyType.ARS}>ARS ($)</SelectItem>
                      <SelectItem value={CurrencyType.USD}>USD (U$D)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taxRate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>% Impuesto</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="21"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      disabled={!form.watch('isTaxable')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="isTaxable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Aplicar impuestos</FormLabel>
                    <FormDescription>
                      {field.value && form.watch('taxRate') > 0
                        ? `Se aplicarán ${form.watch('taxRate')}% de impuestos sobre el precio base`
                        : 'No se aplicarán impuestos sobre el precio base'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked)
                        if (!checked) {
                          form.setValue('taxRate', 0)
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isInvoiceable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-5 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Facturación automática</FormLabel>
                    <FormDescription>
                      {field.value
                        ? 'Al generarse un pago de este servicio, por defecto se generará una factura A o B'
                        : 'Al generarse un pago de este servicio, por defecto se generará un recibo X'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasMultipleOffered"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-5 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Contratos extensibles a esquema familiar</FormLabel>
                    <FormDescription>
                      {field.value
                        ? 'Este servicio permite extender el contrato a miembros familiares'
                        : 'Este servicio no permite extender el contrato a miembros familiares'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasPaymentPlanAvailable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-5 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Ofrece planes de pago</FormLabel>
                    <FormDescription>
                      {field.value
                        ? 'Este servicio ofrece planes de pago financiados'
                        : 'Este servicio no ofrece planes de pago financiados'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasAgePricing"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-5 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Precios por edad</FormLabel>
                    <FormDescription>
                      {field.value
                        ? 'Este servicio tiene diferentes precios según la edad del cliente'
                        : 'Este servicio no tiene precios diferenciados por edad'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isAdditional"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-5 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">{field.value ? 'Es servicio adicional' : 'Es servicio principal'}</FormLabel>
                    <FormDescription>
                      {field.value
                        ? 'Este servicio se considera como un adicional a otros servicios, se anexa a contrataciones existentes'
                        : 'Este servicio es un servicio principal, se utiliza para crear nuevas contrataciones'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-between gap-4">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          )}
          <div className="flex gap-4 ml-auto">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
              disabled={isSubmitting}
            >
              Restablecer
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                submitButtonText
              )}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};
